import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Control, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Save,
  Plus,
  X,
  Image as ImageIcon,
  Palette,
  Type,
  Layout,
  Layers,
  Wrench,
  Globe,
  Star,
  LayoutTemplate,
  Wand2,
  Move,
  Loader2
} from 'lucide-react';
import { ImageUpload } from '@/components/ui/image-upload';
import { ProjectScreenshotPreview } from '@/components/ui/project-screenshot-preview';
import { ScrollingScreenshotPreview } from '@/components/ui/scrolling-screenshot-preview';
import { projects } from '@/data/projects';
import { useCreateProject, useUpdateProject, useProjectById } from '@/hooks/use-projects';
import { useClients } from '@/hooks/use-clients';
import { useToast } from '@/hooks/use-toast';
import { useUpload } from '@/hooks/use-upload';
import { extractColorsFromImage } from '@/lib/color-extraction';
import { generateProjectFields } from '@/lib/openrouter';
import { AIGenerateButton } from '@/components/ui/ai-generate-button';

// Schema Definition
const projectSchema = z.object({
  title: z.string().min(1, 'Título é obrigatório'),
  slug: z.string().min(1, 'Slug é obrigatório'),
  subtitle: z.string().min(1, 'Subtítulo é obrigatório'),
  description: z.string().optional(),
  client: z.any(),
  year: z.string().min(4, 'Ano é obrigatório'),
  cover_image: z.string().min(1, 'Imagem de capa é obrigatória'),
  gallery: z.array(z.string()).default([]),
  disciplines: z.array(z.string()).default([]),
  roles: z.array(z.string()).default([]),
  tools: z.array(z.string()).default([]),
  color_palette: z.array(z.string()).default([]),
  typography: z.array(z.string()).default([]),
  featured: z.boolean().default(false),
  visible: z.boolean().default(true),
  auto_scroll: z.boolean().default(false),
  cover_position: z.enum(['top', 'center', 'bottom']).default('top'),
});

type ProjectFormData = z.infer<typeof projectSchema>;

const ProjetoForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const { toast } = useToast();
  const { uploadImage, base64ToFile } = useUpload();

  const { data: projectData, isLoading: projectLoading } = useProjectById(id || '');
  const { data: clientsList = [] } = useClients(); // Renamed to avoid confusion if needed

  // Use mock projects if API fails or for initial load if needed, but ideally rely on hook
  // const project = isEditing ? projects.find(p => p.id === id) : null; 
  // Let's rely on projectData if available
  const project = projectData;

  const createProject = useCreateProject();
  const updateProject = useUpdateProject();

  const [isLoading, setIsLoading] = useState(false);
  const [isExtractingColors, setIsExtractingColors] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
    reset // Added reset
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      title: '',
      slug: '',
      subtitle: '',
      description: '',
      client: '',
      year: new Date().getFullYear().toString(),
      cover_image: '',
      gallery: [],
      disciplines: [],
      roles: [],
      tools: [],
      color_palette: [],
      typography: [],
      featured: false,
      visible: true,
      auto_scroll: false,
      cover_position: 'top',
    },
  });

  const watchedValues = watch();

  // Reset form when data loads
  useEffect(() => {
    if (project) {
      // Adapt API data to Form Data
      reset({
        title: project.title || '',
        slug: project.slug || '',
        subtitle: (project as any).subtitle || project.summary || '', // Map summary to subtitle if needed
        description: (project as any).description || project.summary || '',
        client: (project as any).client || '',
        year: String(project.year || new Date().getFullYear()),
        cover_image: (project as any).coverImage || (project as any).cover_image || '',
        gallery: (project as any).gallery?.map((g: any) => typeof g === 'string' ? g : g.src) || [],
        disciplines: (project as any).disciplines || [],
        roles: (project as any).roles || [],
        tools: (project as any).tools || [],
        color_palette: (project as any).colors || (project as any).color_palette || [],
        typography: (project as any).typography || [],
        featured: project.featured || false,
        visible: (project as any).visible !== false,
        auto_scroll: (project as any).auto_scroll || false,
        cover_position: (project as any).cover_position || 'top',
      });
    }
  }, [project, reset]);


  // Auto-generate slug
  useEffect(() => {
    if (!isEditing && watchedValues.title) {
      const slug = watchedValues.title
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
      setValue('slug', slug);
    }
  }, [watchedValues.title, isEditing, setValue]);

  const onSubmit = async (data: ProjectFormData) => {
    setIsLoading(true);
    try {
      // Upload Cover Image if Base64
      let coverImageUrl = data.cover_image;
      if (coverImageUrl.startsWith('data:')) {
        const file = await base64ToFile(coverImageUrl, `project-cover-${Date.now()}.png`);
        const uploaded = await uploadImage(file, 'images');
        if (uploaded) coverImageUrl = uploaded;
      }

      // Upload Gallery Images if Base64
      const galleryUrls = await Promise.all(
        data.gallery.map(async (img, idx) => {
          if (img.startsWith('data:')) {
            const file = await base64ToFile(img, `project-gallery-${Date.now()}-${idx}.png`);
            return await uploadImage(file, 'images') || img; // Fallback to original if fail (though ideally should fail hard)
          }
          return img;
        })
      );

      // Prepare data for API
      const apiData: any = {
        ...data,
        year: parseInt(data.year),
        coverImage: coverImageUrl, // Map back
        gallery: galleryUrls,
        colors: data.color_palette, // Map back
        summary: data.subtitle || data.description || '', // Map subtitle to summary for DB
        auto_scroll: data.auto_scroll,
        cover_position: data.cover_position,
        // client logic needs to handle ID vs Name depending on backend
      };

      if (isEditing) {
        await updateProject.mutateAsync({ id: id!, updates: apiData });
        toast({ title: "Sucesso", description: "Projeto atualizado com sucesso" });
      } else {
        await createProject.mutateAsync(apiData);
        toast({ title: "Sucesso", description: "Projeto criado com sucesso" });
      }

      navigate('/admin/projetos');
    } catch (error) {
      console.error('Erro ao salvar projeto:', error);
      toast({ title: "Erro", description: "Falha ao salvar projeto", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  // Helper for array fields
  const handleAddItem = (field: keyof ProjectFormData, value: string, setter: (val: string) => void) => {
    if (value.trim()) {
      const current = (watchedValues[field] as string[]) || [];
      if (!current.includes(value.trim())) {
        setValue(field, [...current, value.trim()], { shouldValidate: true });
        setter('');
      }
    }
  };

  const handleRemoveItem = (field: keyof ProjectFormData, index: number) => {
    const current = (watchedValues[field] as string[]) || [];
    setValue(field, current.filter((_: any, i: number) => i !== index), { shouldValidate: true });
  };

  // Extract colors from cover image
  const handleExtractColors = async () => {
    if (!watchedValues.cover_image) {
      toast({ title: "Aviso", description: "Adicione uma imagem de capa primeiro", variant: "destructive" });
      return;
    }

    setIsExtractingColors(true);
    try {
      const colors = await extractColorsFromImage(watchedValues.cover_image, 6);
      if (colors.length > 0) {
        setValue('color_palette', colors, { shouldValidate: true });
        toast({
          title: "Sucesso",
          description: `${colors.length} cores extraídas da imagem!`
        });
      } else {
        toast({
          title: "Aviso",
          description: "Não foi possível extrair cores da imagem",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Erro ao extrair cores:', error);
      toast({
        title: "Erro",
        description: "Falha ao analisar a imagem",
        variant: "destructive"
      });
    } finally {
      setIsExtractingColors(false);
    }
  };

  // Generate project fields with AI
  const handleGenerateWithAI = async () => {
    if (!watchedValues.cover_image) {
      toast({
        title: "Aviso",
        description: "Adicione uma imagem de capa primeiro para a IA analisar",
        variant: "destructive"
      });
      return;
    }

    setIsGeneratingAI(true);
    try {
      const generated = await generateProjectFields(
        watchedValues.cover_image,
        watchedValues.title || undefined
      );

      // Fill in the fields
      if (generated.title && !watchedValues.title) {
        setValue('title', generated.title, { shouldValidate: true });
      }
      if (generated.subtitle) {
        setValue('subtitle', generated.subtitle, { shouldValidate: true });
      }
      if (generated.description) {
        setValue('description', generated.description, { shouldValidate: true });
      }
      if (generated.disciplines?.length) {
        setValue('disciplines', generated.disciplines, { shouldValidate: true });
      }
      if (generated.tools?.length) {
        setValue('tools', generated.tools, { shouldValidate: true });
      }
      if (generated.roles?.length) {
        setValue('roles', generated.roles, { shouldValidate: true });
      }

      toast({
        title: "✨ Campos preenchidos!",
        description: "A IA analisou a imagem e preencheu os campos. Revise o conteúdo."
      });
    } catch (error) {
      console.error('Erro ao gerar com IA:', error);
      toast({
        title: "Erro",
        description: "Falha ao gerar conteúdo com IA. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // Render helper for array inputs
  const [itemInputs, setItemInputs] = useState<Record<string, string>>({});

  const setInput = (field: string, val: string) => {
    setItemInputs(prev => ({ ...prev, [field]: val }));
  };

  // Generic Array Section Renderer
  const renderArraySection = (
    field: keyof ProjectFormData,
    label: string,
    placeholder: string,
    Icon: any
  ) => {
    const values = (watchedValues[field] as string[]) || [];
    const inputVal = itemInputs[field as string] || '';

    return (
      <div className="space-y-3">
        <Label className="text-sm font-medium">{label}</Label>
        <div className="flex space-x-2">
          <div className="relative flex-1">
            <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={placeholder}
              value={inputVal}
              onChange={(e) => setInput(field as string, e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddItem(field, inputVal, (v) => setInput(field as string, v)))}
              className="pl-10"
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={() => handleAddItem(field, inputVal, (v) => setInput(field as string, v))}
            disabled={!inputVal.trim()}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        {values.length > 0 ? (
          <div className="flex flex-wrap gap-2 mt-3 p-3 bg-muted/30 rounded-lg border border-border/50">
            {values.map((item, index) => (
              <Badge key={index} variant="secondary" className="pl-2 pr-1 py-1 flex items-center gap-1 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                {field === 'color_palette' ? (
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full border shadow-sm" style={{ backgroundColor: item }}></span>
                    <span>{item}</span>
                  </div>
                ) : (
                  <span>{item}</span>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 ml-1 hover:bg-red-100 hover:text-red-600 rounded-full"
                  onClick={() => handleRemoveItem(field, index)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground pl-1 italic">Nenhum item adicionado.</p>
        )}
        {errors[field] && (
          <p className="text-sm text-destructive">{errors[field]?.message as string}</p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-md pb-4 pt-2 border-b mb-6 -mx-6 px-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/projetos')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-3xl font-bold">
              {isEditing ? 'Editar Projeto' : 'Novo Projeto'}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground mr-4">
            {watchedValues.visible ? (
              <Badge variant="outline" className="border-green-500 text-green-500">Publicado</Badge>
            ) : (
              <Badge variant="outline" className="text-muted-foreground">Rascunho</Badge>
            )}
          </div>
          <AIGenerateButton
            onClick={handleGenerateWithAI}
            isLoading={isGeneratingAI}
            disabled={!watchedValues.cover_image}
            label="✨ Preencher com IA"
            loadingLabel="Analisando..."
          />
          <Button onClick={handleSubmit(onSubmit)} disabled={isLoading} className="bg-blue-600 hover:bg-blue-700">
            <Save className="w-4 h-4 mr-2" />
            {isLoading ? 'Salvando...' : 'Salvar Projeto'}
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">

          {/* Main Content */}
          <div className="xl:col-span-2 space-y-8">

            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Layout className="w-5 h-5 text-blue-500" />
                  <span>Informações do Projeto</span>
                </CardTitle>
                <CardDescription>Dados principais de identificação</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Título do Projeto *</Label>
                    <Input id="title" {...register('title')} placeholder="Ex: E-commerce Redesign" className="text-lg font-medium" />
                    {errors.title && <p className="text-sm text-destructive">{errors.title.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="slug">Slug (URL) *</Label>
                    <Input id="slug" {...register('slug')} placeholder="ex-ecommerce-redesign" className="font-mono text-sm" />
                    {errors.slug && <p className="text-sm text-destructive">{errors.slug.message}</p>}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subtitle">Subtítulo / Categoria *</Label>
                  <Input id="subtitle" {...register('subtitle')} placeholder="Ex: Design System & UI Kit" />
                  {errors.subtitle && <p className="text-sm text-destructive">{errors.subtitle.message}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Descrição Completa</Label>
                  <Textarea
                    id="description"
                    {...register('description')}
                    placeholder="Contexto, desafio e solução do projeto..."
                    rows={6}
                    className="resize-y"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Media */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <ImageIcon className="w-5 h-5 text-blue-500" />
                  <span>Mídia & Galeria</span>
                </CardTitle>
                <CardDescription>Imagens do projeto</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <Label>Imagem de Capa (Cover) *</Label>
                  <div className="p-1 border-2 border-dashed border-border rounded-xl">
                    <ImageUpload
                      value={watchedValues.cover_image}
                      onChange={(url) => setValue('cover_image', url, { shouldValidate: true })}
                      label="Upload da Capa"
                    />
                  </div>
                  {errors.cover_image && <p className="text-sm text-destructive">{errors.cover_image.message}</p>}
                </div>

                {watchedValues.cover_image && (
                  <div className="space-y-3">
                    <Label>Preview do Screenshot</Label>
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="space-y-0.5">
                        <Label className="text-base">Auto-scroll</Label>
                        <p className="text-xs text-muted-foreground">Rolagem automática do screenshot</p>
                      </div>
                      <Switch
                        checked={watchedValues.auto_scroll}
                        onCheckedChange={(checked) => setValue('auto_scroll', checked)}
                        className="data-[state=checked]:bg-blue-600"
                      />
                    </div>

                    {/* Cover Position Selector */}
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <Move className="w-4 h-4 text-muted-foreground" />
                        <Label className="text-base">Posição da Capa</Label>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">
                        Como a imagem deve ser posicionada nos cards e previews
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {(['top', 'center', 'bottom'] as const).map((position) => (
                          <button
                            key={position}
                            type="button"
                            onClick={() => setValue('cover_position', position)}
                            className={`
                              p-3 rounded-lg text-sm font-medium transition-all
                              ${watchedValues.cover_position === position
                                ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                                : 'bg-white/5 text-muted-foreground hover:bg-white/10 border border-border/50'
                              }
                            `}
                          >
                            {position === 'top' && '⬆️ Topo'}
                            {position === 'center' && '↔️ Centro'}
                            {position === 'bottom' && '⬇️ Base'}
                          </button>
                        ))}
                      </div>
                    </div>

                    <ProjectScreenshotPreview
                      src={watchedValues.cover_image}
                      alt={`Screenshot de ${watchedValues.title || 'projeto'}`}
                      enableAutoScroll={watchedValues.auto_scroll}
                      onToggleAutoScroll={(enabled) => setValue('auto_scroll', enabled)}
                      height="400px"
                    />
                  </div>
                )}

                <Separator />

                <div className="space-y-3">
                  <Label>Galeria do Projeto</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {watchedValues.gallery?.map((url, index) => (
                      <div key={index} className="relative aspect-square group">
                        <img src={url} alt={`Gallery ${index}`} className="w-full h-full object-cover rounded-lg border" />
                        <button
                          type="button"
                          onClick={() => handleRemoveItem('gallery', index)}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <div className="aspect-square flex items-center justify-center bg-muted/50 rounded-lg border-2 border-dashed border-muted-foreground/20 hover:bg-muted/80 transition-colors">
                      <ImageUpload
                        value=""
                        onChange={(url) => handleAddItem('gallery', url, () => { })}
                        label="+"
                        className="w-full h-full"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Layers className="w-5 h-5 text-blue-500" />
                  <span>Detalhes Técnicos</span>
                </CardTitle>
                <CardDescription>Tecnologias, funções e disciplinas</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {renderArraySection('disciplines', 'Disciplinas', 'Ex: UI Design, Frontend', Layers)}
                {renderArraySection('roles', 'Minhas Funções', 'Ex: Lead Designer, Developer', Star)}
                {renderArraySection('tools', 'Ferramentas / Tech Stack', 'Ex: Figma, React, Tailwind', Wrench)}

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <Palette className="w-4 h-4 text-muted-foreground" />
                      Paleta de Cores (Hex)
                    </Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleExtractColors}
                      disabled={isExtractingColors || !watchedValues.cover_image}
                      className="h-8 gap-2 text-xs bg-gradient-to-r from-blue-500/10 to-purple-500/10 hover:from-blue-500/20 hover:to-purple-500/20 border-blue-500/30"
                    >
                      {isExtractingColors ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Analisando...
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-3 h-3" />
                          Extrair da Imagem
                        </>
                      )}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="#000000"
                      value={itemInputs['color_palette'] || ''}
                      onChange={(e) => setInput('color_palette', e.target.value)}
                      className="font-mono uppercase"
                      maxLength={7}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleAddItem('color_palette', itemInputs['color_palette'] || '', (v) => setInput('color_palette', v))}
                    >
                      Add
                    </Button>
                  </div>
                  {watchedValues.color_palette && watchedValues.color_palette.length > 0 ? (
                    <div className="flex flex-wrap gap-3 mt-3 p-4 bg-muted/30 rounded-xl border border-border/50">
                      {watchedValues.color_palette.map((color, idx) => (
                        <div
                          key={idx}
                          className="group relative"
                        >
                          <div
                            className="w-10 h-10 rounded-xl border-2 border-white/20 shadow-lg cursor-help transition-transform hover:scale-110"
                            style={{ backgroundColor: color }}
                            title={color}
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveItem('color_palette', idx)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white w-5 h-5 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 text-xs font-bold transition-opacity shadow-md"
                          >
                            ×
                          </button>
                          <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] font-mono text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                            {color}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground pl-1 italic">
                      Nenhuma cor adicionada. Use o botão "Extrair da Imagem" para identificar automaticamente.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

          </div>

          {/* Sidebar */}
          <div className="space-y-6">

            {/* Publishing Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Globe className="w-5 h-5 text-blue-500" />
                  <span>Publicação</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="space-y-0.5">
                      <Label className="text-base">Visibilidade</Label>
                      <p className="text-xs text-muted-foreground">Exibir publicamente</p>
                    </div>
                    <Switch
                      checked={watchedValues.visible}
                      onCheckedChange={(checked) => setValue('visible', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="space-y-0.5">
                      <Label className="text-base">Destaque</Label>
                      <p className="text-xs text-muted-foreground">Exibir na Home</p>
                    </div>
                    <Switch
                      checked={watchedValues.featured}
                      onCheckedChange={(checked) => setValue('featured', checked)}
                    />
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Cliente</Label>
                    <Select
                      value={watchedValues.client || 'none'}
                      onValueChange={(value) => setValue('client', value === 'none' ? '' : value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Selecione um cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nenhum cliente</SelectItem>
                        {clientsList.map((client: any) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Ano</Label>
                    <Input {...register('year')} placeholder="2024" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Typography */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Type className="w-5 h-5 text-blue-500" />
                  <span>Tipografia</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {renderArraySection('typography', 'Fontes', 'Ex: Inter, Roboto', Type)}
              </CardContent>
            </Card>

            {/* Preview Card */}
            <Card className="bg-slate-950 border-slate-800 overflow-hidden sticky top-24">
              <CardHeader className="border-b border-slate-800 pb-4">
                <CardTitle className="flex items-center space-x-2 text-white">
                  <LayoutTemplate className="w-5 h-5 text-blue-400" />
                  <span>Live Preview</span>
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Visualização do Card
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 bg-slate-900 flex justify-center">
                <div className="w-full max-w-[320px]">
                  {/* Simplified Preview simulating Project Card */}
                  <div className="bg-background rounded-xl overflow-hidden shadow-lg border">
                    <div className="aspect-[4/3] bg-muted relative">
                      {watchedValues.cover_image ? (
                        <img
                          src={watchedValues.cover_image}
                          className="w-full h-full object-cover"
                          style={{
                            objectPosition: watchedValues.cover_position === 'top'
                              ? 'top'
                              : watchedValues.cover_position === 'bottom'
                                ? 'bottom'
                                : 'center'
                          }}
                          alt="Preview"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">Sem imagem</div>
                      )}
                      <div className="absolute top-2 right-2 flex gap-1">
                        {watchedValues.featured && <Badge className="bg-yellow-500/90 hover:bg-yellow-500">Destaque</Badge>}
                      </div>
                    </div>
                    <div className="p-4 space-y-2">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
                        {watchedValues.disciplines?.[0] || 'Disciplina'}
                      </p>
                      <h3 className="font-bold text-lg leading-tight">{watchedValues.title || 'Título do Projeto'}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {watchedValues.subtitle || 'Subtítulo do projeto...'}
                      </p>
                      <div className="pt-2 flex flex-wrap gap-1">
                        {watchedValues.tools?.slice(0, 3).map((t, i) => (
                          <span key={i} className="text-[10px] px-1.5 py-0.5 bg-secondary rounded">{t}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </form>
    </div>
  );
};

export default ProjetoForm;
